// src/prints/TreHistory_print.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import { useParams } from "react-router-dom";
import axios from "axios";
import Logo from "../assets/logo.png";
import Swal from "sweetalert2";

const API_BASE =
  import.meta.env?.VITE_API_BASE ||
  process.env.REACT_APP_API_BASE ||
  "https://rfs2.infraconsoft.net.in/api";

const API_ORIGIN = API_BASE.replace(/\/api\/?$/, "");

const TreHistory_print = () => {
  useAuthRedirect();
  const { id } = useParams();

  const branch = (localStorage.getItem("branch") || "").trim();
  const user = (localStorage.getItem("username") || "").trim();

  // ---- Header/meta ----
  const [site, setSite] = useState({
    ok: true,
    name: "Infraconsoft",
    logo: null,
    address: "",
    email: "",
    phone: "",
    _ts: 0,
  });

  const [header, setHeader] = useState({
    tyre_model: "N/A",
    vehicle_name: "N/A",
    tyre_amount: "N/A",
    tyre_make: "N/A",
    tyre_seriolno: "N/A",
    tyre_type: "N/A",
  });

  // ---- Abstract payload ----
  const [absData, setAbsData] = useState(null);
  const [loading, setLoading] = useState(true);

  // helpers
  const fmtDate = (d) => {
    if (!d) return "—";
    // handle string/Date
    const dt = typeof d === "string" || typeof d === "number" ? new Date(d) : d;
    if (isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString("en-GB");
  };
  const toNum = (v) => (v == null ? null : Number(v));

  const logoSrc = useMemo(() => {
    const src = site.logo ? `${site.logo}${site._ts ? `?v=${site._ts}` : ""}` : Logo;
    return src;
  }, [site.logo, site._ts]);

  useEffect(() => {
    let ignore = false;

    const loadAll = async () => {
      setLoading(true);
      const show = () =>
        Swal.fire({
          title: "Loading Tyre History",
          text: "Please wait…",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
          showConfirmButton: false,
        });
      const close = () => Swal.close();

      try {
        show();

        // 1) Site info (branding)
        try {
          const res = await axios.get(`${API_BASE}/siteinfo`);
          const s = res?.data || {};
          let logo = s.logo || null;
          if (logo && !/^https?:\/\//i.test(logo)) {
            if (!logo.startsWith("/")) logo = `/${logo}`;
            logo = `${API_ORIGIN}${logo}`;
          }
          if (!ignore) {
            setSite({
              ok: !!s.ok,
              name: s.name || "Infraconsoft",
              logo,
              address: s.address || "",
              email: s.email || "",
              phone: s.phone || "",
              _ts: Date.now(),
            });
          }
        } catch (e) {
          // keep defaults
          console.warn("siteinfo failed", e);
        }

        // 2) Tyre header/meta
        try {
          const hdr = await axios.get(
            `${API_BASE}/tyreCurrentStatus_headerDetails`,
            { params: { id, branch } }
          );
          const item = hdr?.data?.headerDetails?.[0];
          if (item && !ignore) {
            setHeader({
              tyre_model: item.tyre_model || "N/A",
              vehicle_name: item.vehicle_name || "N/A",
              tyre_amount: item.tyre_amount || "N/A",
              tyre_make: item.tyre_make || "N/A",
              tyre_seriolno: item.tyre_seriolno || "N/A",
              tyre_type: item.tyre_type || "N/A",
            });
          }
        } catch (e) {
          console.warn("headerDetails failed", e);
        }

        // 3) Abstract (single payload with all math)
        const abs = await axios.get(`${API_BASE}/tyreHistory_abstract/${id}`);
        if (!ignore) setAbsData(abs.data);

      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Unable to load tyre history.",
        });
      } finally {
        close();
        setLoading(false);
      }
    };

    if (id && branch) loadAll();
    return () => {
      ignore = true;
    };
  }, [id, branch]);

  // Derived display bits
  const dominantBasis = absData?.basisSummary?.dominant || "Mixed";
  const totals = absData?.totals || {
    total_expenses: 0,
    total_km: 0,
    total_hours: 0,
    cost_per_km: null,
    cost_per_hour: null,
  };
  const counts = absData?.counts || {
    cycles: 0,
    rtd: 0,
    original: 0,
    open_cycles: 0,
    closed_cycles: 0,
  };
  const performance = absData?.performance || {
    percent: null,
    basis: null,
    note: null,
  };

  const performanceGrade = useMemo(() => {
    const p = Number(performance?.percent || 0);
    if (!p) return null;
    if (p > 75) return "Excellent";
    if (p >= 61) return "Good";
    if (p >= 41) return "Average";
    if (p >= 10) return "Poor";
    return "Poor";
  }, [performance]);

  const formatNum = (n, dec = 2) =>
    n == null ? "—" : Number(n).toFixed(dec);

  const list = absData?.cycles || [];

  const basisLabel = (b) => (b === "Hour" ? "Hrs" : "Km");
  const dominantCostLabel =
    dominantBasis === "Hour" ? "Cost Per Hour" : dominantBasis === "Km" ? "Cost Per Km" : "Cost Per Km/Hr";

  return (
    <div className="delivery-note-container">
      {/* Header Section */}
      <div className="header">
        <div className="logo">
          <img src={logoSrc} alt={`${site.name} Logo`} />
        </div>
        <div className="company-info">
          <h2 style={{ textTransform: "uppercase" }}>
            {site.name || "Infraconsoft"}
          </h2>
          <p>
            {site.address && (
              <span>
                {site.address}.<br />
              </span>
            )}
            {site.phone && <span>Ph: {site.phone}</span>}
            {site.phone && site.email && <span> | </span>}
            {site.email && (
              <span>
                <strong>E-mail: {site.email}</strong>
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Tyre Meta */}
      <div className="delivery-details">
        <div className="left">
          <p><strong>Tyre(Front/Rear):</strong> {header.tyre_type}</p>
          <p><strong>Serial No:</strong> {header.tyre_seriolno}</p>
          <p><strong>Model:</strong> {header.tyre_model}</p>
        </div>
        <div className="right">
          <p><strong>Make:</strong> {header.tyre_make}</p>
          <p><strong>Rate(New):</strong> {header.tyre_amount}</p>
        </div>
      </div>

      {/* Items Table */}
      <table className="items-table">
        <thead>
          <tr>
            <th>S.No</th>
            <th>Equ.Name</th>
            <th>Branch</th>
            <th>Original/RTD</th>
            <th>Fitting Date</th>
            <th>Fitting Hrs/Km</th>
            <th>Removing Date</th>
            <th>Removing Km/Hrs</th>
            <th>Running Km/Hrs</th>
            <th>Rate</th>
            <th>Tyre Cost Km/Hrs</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {!loading && list.length === 0 && (
            <tr>
              <td colSpan={12} style={{ textAlign: "center" }}>
                No records found
              </td>
            </tr>
          )}
          {list.map((row, idx) => {
            const isOpen = !row.remove_date;
            const basisL = basisLabel(row.basis);
            return (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td>{row.vehicle || "—"}</td>
                <td>{row.branch || "—"}</td>
                <td>{row.is_retread ? "RTD" : "Original"}</td>
                <td>{fmtDate(row.fitting_date)}</td>
                <td>{row.fitting_value ?? "—"}</td>

                {isOpen ? (
                  <>
                    <td colSpan={2} style={{ color: "red", fontWeight: "bold" }}>
                      Not yet removed
                    </td>
                  </>
                ) : (
                  <>
                    <td>{fmtDate(row.remove_date)}</td>
                    <td>{row.remove_value ?? "—"}</td>
                  </>
                )}

                <td>
                  {row.running != null ? `${formatNum(row.running)} ${basisL}` : "—"}
                </td>

                <td>{row.rate != null ? formatNum(row.rate) : "—"}</td>
                <td>
                  {row.cost_per_unit != null ? `${formatNum(row.cost_per_unit, 4)} / ${basisL}` : "—"}
                </td>
                <td>{row.remarks_hint || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Abstract */}
      <h2>Abstract</h2>
      <table className="items-table bill">
        <tbody>
          <tr>
            <td><strong>Total Expenses :</strong></td>
            <td>{formatNum(totals.total_expenses)}</td>
          </tr>

          {/* Show both totals when available to avoid unit mixing */}
          {Number(totals.total_km) > 0 && (
            <tr>
              <td><strong>Total Working (Km) :</strong></td>
              <td>{formatNum(totals.total_km)}</td>
            </tr>
          )}
          {Number(totals.total_hours) > 0 && (
            <tr>
              <td><strong>Total Working (Hours) :</strong></td>
              <td>{formatNum(totals.total_hours)}</td>
            </tr>
          )}

          <tr>
            <td>
              <strong>{dominantCostLabel} :</strong>
            </td>
            <td>
              {dominantBasis === "Hour"
                ? (totals.cost_per_hour != null ? `${formatNum(totals.cost_per_hour, 4)} / Hr` : "—")
                : dominantBasis === "Km"
                ? (totals.cost_per_km != null ? `${formatNum(totals.cost_per_km, 4)} / Km` : "—")
                : (() => {
                    const parts = [];
                    if (totals.cost_per_km != null) parts.push(`${formatNum(totals.cost_per_km, 4)} / Km`);
                    if (totals.cost_per_hour != null) parts.push(`${formatNum(totals.cost_per_hour, 4)} / Hr`);
                    return parts.length ? parts.join("  |  ") : "—";
                  })()
              }
            </td>
          </tr>

          <tr>
            <td><strong>No.Of RTD :</strong></td>
            <td>{counts.rtd ?? 0}</td>
          </tr>

          <tr>
            <td><strong>Performance(%) :</strong></td>
            <td>
              {performance?.percent != null ? (
                <>
                  {formatNum(performance.percent, 2)}%
                  {performanceGrade ? ` (${performanceGrade})` : ""}{" "}
                  {performance?.basis ? `on ${performance.basis}` : ""}
                </>
              ) : (
                <>
                  {/* Legend (as per your original message) */}
                  If 10% to 40% (Poor)
                  <br />
                  If 41% to 60% (Average)
                  <br />
                  If 61% to 75% (Good)
                  <br />
                  If above 75% (Excellent)
                  {performance?.note ? (
                    <>
                      <br />
                      <em style={{ color: "#888" }}>{performance.note}</em>
                    </>
                  ) : null}
                </>
              )}
            </td>
          </tr>

          <tr>
            <td><strong>Approved By :</strong></td>
            <td>{user || "—"}</td>
          </tr>

          <tr>
            <td><strong>Remarks :</strong></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={() => window.print()}
          style={{
            backgroundColor: "#4a90e2",
            color: "white",
            border: "none",
            padding: "12px 24px",
            fontSize: "16px",
            cursor: "pointer",
            borderRadius: "8px",
            transition: "background 0.3s ease-in-out",
            marginTop: "20px",
          }}
        >
          Print
        </button>
      </div>
    </div>
  );
};

export default TreHistory_print;
